import { defineComponent as _defineComponent } from 'vue'
import {
  Payment,
  BankingScheme,
  Beneficiary,
  PaymentType,
  PaymentReport,
  FeeChargeType,
  paymentTypeLabels,
  FeePaymentType,
  Client,
  Trade,
  GenericErrorCodes,
  PaginatedParams,
  Wallet,
  PaymentErrorCodes,
  getBuyCcy,
} from 'ah-api-gateways';
import { makeFormModel, submitForm } from 'ah-common-lib/src/form/helpers';
import { scrollToValidation } from 'ah-common-lib/src/form/helpers';
import { FormValidation, FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { computed, reactive, watch, onBeforeMount, ref, inject } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { paymentCurrencyForm, paymentPurposeForm } from 'ah-payments/src/formSchemas/paymentFormSchemas';
import { getChildModel, setState } from 'ah-common-lib/src/form/helpers';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { usePaymentState } from 'ah-payments';
import { FieldOption } from 'ah-common-lib/src/form/interfaces';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import TradeBeneficiaryInfo from 'ah-trades/src/components/info/TradeBeneficiaryInfo.vue';
import CurrencyFundingFee from 'ah-trades/src/components/fees/CurrencyFundingFee.vue';
import PaymentReferenceForm from './PaymentReferenceForm.vue';
import PaymentReasonForm from './PaymentReasonForm.vue';
import BeneficiaryAccountSelector from 'ah-beneficiaries/src/components/BeneficiaryAccountSelector.vue';
import SchedulePaymentForm from './SchedulePaymentForm.vue';
import ChargeTypeForm from './ChargeTypeForm.vue';
import { ON_BEHALF_OF_CLIENT_INJECT_KEY } from 'ah-common-lib/src/onBehalfOf/constants';
import PaymentEditorCancelModal from './modals/PaymentEditorCancelModal.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentEditor',
  props: {
    payment: null
  },
  emits: ["update:validations", "update-payment", "cancel", "update-title"],
  setup(__props: any, { emit }: { emit: ({
  /**
   * - update:validations: emmited when `validations` changes. Can be used with .sync
   */
  (e: 'update:validations', value: FormValidation[]): void;
  /**
   * update-payment: emmited when a payment is saved/created. Can be used with .sync
   */
  (e: 'update-payment', value: Payment): void;

  (e: 'cancel'): void;

  (e: 'update-title', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /**
   * Payment to update, if any
   *
   * If unset, a new payment will be created
   * Can be updated via .sync
   */
  payment?: PaymentReport;
};





const requestManager = useRequestManager({
  exposeToParent: true,
}).manager;

const errorMessages: { [key: string]: string } = {
  [PaymentErrorCodes.PAYMENT_LINKED_TO_OTHER_TRADE]: 'Trade is already linked to another Payment',
  [PaymentErrorCodes.MAX_PAYMENTS_ON_TRADES]: 'The maximum number of payments allowed on a trade is 10',
  [PaymentErrorCodes.PAYMENT_EXCEEDS_TRADE_AMOUNT]: 'The payment amount exceeds the remaining trade amount',
  formError: 'Form contains errors, please review values entered.',
  generic: 'An unexpected problem has occurred. Please try again later.',
};

const paymentState = usePaymentState();

const onBehalfOfClient = useOnBehalfOf();

const authStore = paymentState.store.useAuthStore();

const chargeTypeForm = ref<InstanceType<typeof ChargeTypeForm> | null>(null);

const paymentReferenceForm = ref<InstanceType<typeof PaymentReferenceForm> | null>(null);

const paymentReasonForm = ref<InstanceType<typeof PaymentReasonForm> | null>(null);

const selectedBeneficiary = ref<Beneficiary | null>(null);

const currentPurposeCodesOptions = ref<{ value: string; label: string }[]>([]);

const selectedPurposeCodeDescription = computed(
  () =>
    currentPurposeCodesOptions.value.find((option) => option.value === paymentPurposeDef.form.paymentPurpose)?.label ??
    paymentPurposeDef.form.paymentPurpose
);

const state = reactive<{
  search: string;
  pageAndSortParams: Partial<PaginatedParams>;
  onBehalfOfClient: Client | null;
  selfBeneficiaries: Beneficiary[] | null;
  paymentType: PaymentType;
  paymentReason: string | null;
  paymentReference: string;
  selectedCurrency: string;
  executionDate: Date | null;
  chargeType?: FeeChargeType | null;
  wallets: Wallet[] | null;
  confirmPayment: boolean;
  trade: Trade | null;
  tradePaymentTotalSum: number;
}>({
  search: '',
  pageAndSortParams: { sort: 'createdAt', sortDirection: 'DESC' },
  onBehalfOfClient: inject(ON_BEHALF_OF_CLIENT_INJECT_KEY, null),
  selfBeneficiaries: null,
  paymentType: PaymentType.IMMEDIATE,
  paymentReason: '',
  paymentReference: '',
  selectedCurrency: '',
  executionDate: null,
  chargeType: null,
  wallets: null,
  confirmPayment: false,
  trade: null,
  tradePaymentTotalSum: 0,
});

const paymentCurrencyDef = reactive<FormDefinition>({
  form: makeFormModel(paymentCurrencyForm()),
  validation: null,
});

const paymentPurposeDef = reactive<FormDefinition>({
  form: makeFormModel(paymentPurposeForm()),
  validation: null,
});

const useChargeType = computed(() => selectedBeneficiary.value?.bankingScheme === BankingScheme.SWIFT);

const bankingScheme = computed(() => selectedBeneficiary.value?.bankingScheme);

const formsInvalid = computed(() => {
  return validations.value.find((f) => !f || f.$invalid);
});

const formsLoaded = computed(() => {
  return validations.value.filter((f) => !f).length === 0;
});

const isSaving = computed(() => {
  return requestManager.requestStates.savePayment === 'pending';
});

const isClient = computed(() => paymentState.store.useAuthStore().isClientUser);

const ownerId = computed(() => {
  if (isClient.value) {
    return paymentState.store.useAuthStore().loggedInIdentity?.client?.id;
  }
  if (!isClient.value) {
    return paymentState.store.useAuthStore().loggedInIdentity?.partner?.id;
  }
  return null;
});

const isPartnerUser = computed(() => !onBehalfOfClient.value && !paymentState.store.useAuthStore().isClientUser);

const validations = computed(() => {
  return [...(props.payment ? [] : [paymentCurrencyDef.validation!])] as FormValidation[];
});

const selectedWallet = computed(() => {
  return state.wallets?.filter((wallet) => {
    return wallet.id === paymentCurrencyDef.form.currency;
  })[0];
});

const isPaymentPurposeRequired = ref(false);

const continueDisabled = computed(() => {
  return (
    !formsLoaded.value ||
    formsInvalid.value ||
    isCurrencyAmountInvalid.value ||
    !selectedBeneficiary.value ||
    (state.paymentType === PaymentType.TRADE_SEND_TO_BENEFICIARY && !state.trade?.id) ||
    (isPaymentPurposeRequired.value && !paymentPurposeDef.form.paymentPurpose) ||
    !state.paymentReason ||
    requestManager.anyPending
  );
});

const tradeAvailableBalance = computed(() => {
  let amount = 0;
  if (state.trade) {
    amount = getBuyCcy(state.trade).remainingClientAmount - state.tradePaymentTotalSum;

    if (props.payment) {
      amount += props.payment.amount;
    }
  }
  return amount;
});

const tradeDisplayBalance = computed(() => {
  let amount = 0;
  if (state.trade) {
    amount = tradeAvailableBalance.value - paymentCurrencyDef.form.amount;
  }
  return formatCurrencyValue(amount);
});

const isCurrencyAmountInvalid = computed(() => paymentCurrencyDef.validation?.$invalid === true);

function confirmPayment() {
  emit('update-title', 'Review and Execute');
  state.confirmPayment = true;
}

function savePayment() {
  submitForms();

  if (formsInvalid.value) {
    scrollToValidation(validations.value.find((f) => !f || f.$invalid)!);
    return;
  }

  const model: Payment = {
    ...formToPaymentModel(),
  };

  Object.keys(model).forEach((key) => {
    if ((model as any)[key] === '') {
      (model as any)[key] = null;
    }
  });

  let request = paymentState.services.payments.createPayment(model, onBehalfOfClient.value?.id, {
    errors: { silent: true },
  });
  if (model.type === PaymentType.SCHEDULED || model.type === PaymentType.TRADE_SEND_TO_BENEFICIARY) {
    request = paymentState.services.payments.createScheduledPayment(model, onBehalfOfClient.value?.id, {
      errors: { silent: true },
    });
  }
  if (props.payment) {
    request = paymentState.services.payments.updateScheduledPayment(
      props.payment.id,
      {
        amount: model.amount,
        chargeType: model.chargeType,
        description: model.description,
        reference: model.reference,
        purposeCode: model.purposeCode,
        executionDate: model.executionDate,
      },
      undefined,
      {
        errors: { silent: true },
      }
    );
  }

  requestManager.currentOrNew('savePayment', request).subscribe(
    () => {
      paymentState.toast.success(`Payment ${props.payment?.id ? 'updated' : 'created'}`);
      emit('update-payment', model);
    },
    (e) => {
      const error = e.response?.data;
      if (error && error.code === GenericErrorCodes.VALIDATION_ERROR) {
        if (error.subErrors && error.subErrors.length) {
          for (let i = 0; i < error.subErrors.length; i++) {
            const subError = error.subErrors[i];
            if (subError.category === 'VALIDATION') {
              setErrorMessage(subError.field, subError.message);
            }
          }
        }
        paymentState.toast.error(errorMessages.formError);
        return;
      }

      paymentState.toast.error(errorMessages[error.code] || errorMessages.generic);
    }
  );
}

function setErrorMessage(fieldName: string, message: string) {
  let field = null;
  if (fieldName === 'currency' || 'amount') {
    field = getChildModel(paymentCurrencyDef.form, fieldName);
  }

  if (field) {
    setState(field, 'errors', [
      {
        name: 'customError',
        error: message,
      },
    ]);
  }
}

function formToPaymentModel(): Payment {
  let payment: Payment = {
    amount: paymentCurrencyDef.form.amount,
    type: state.paymentType,
    beneficiaryId: selectedBeneficiary.value!.id,
    reference: state.paymentReference || undefined,
    description: state.paymentReason || undefined,
    oboClientId: state.onBehalfOfClient?.id,
  } as any;

  if (!props.payment && selectedWallet.value?.id) {
    payment.walletId = selectedWallet.value?.id;
  }

  if (selectedBeneficiary.value?.bankingScheme === BankingScheme.SWIFT) {
    payment.chargeType = state.chargeType!;
  }

  if (payment.type === PaymentType.SCHEDULED) {
    payment.executionDate = state.executionDate ? formatDate(state.executionDate, 'yyyy-MM-dd') : '';
  }

  if (selectedWallet.value?.currency === 'CNY' || selectedWallet.value?.currency === 'DXB') {
    payment.purposeCode = paymentPurposeDef.form.paymentPurpose;
  }

  if (payment.type === PaymentType.TRADE_SEND_TO_BENEFICIARY) {
    payment.tradeId = state.trade?.id ?? '';
  }

  return payment;
}

function submitForms() {
  validations.value!.forEach((f) => {
    if (f) {
      submitForm(f);
    }
  });
}

function loadWallets() {
  const query: any = {};
  query.pageSize = 100;
  if (isClient.value) {
    query.owner = {
      isPartnerUser: isPartnerUser.value,
      id: ownerId.value,
    };
  }
  if (!isClient.value) {
    query.partnerWallet = true;
    query.partnerId = authStore.loggedInIdentity?.partner.id;
  }
  requestManager
    .currentOrNew('getWallets', paymentState.services.wallet.listWallets(query))
    .subscribe((response) => (state.wallets = response.list));
}

function setFormData() {
  if (!props.payment) {
    return;
  }
  paymentCurrencyDef.form.currency = props.payment.walletId;
  paymentCurrencyDef.form.amount = props.payment.amount;
  state.paymentReference = props.payment.reference;

  state.paymentType = props.payment.type;
  state.chargeType = props.payment.chargeType;

  if (props.payment.type === PaymentType.SCHEDULED && props.payment.executionDate) {
    let dateArray = props.payment.executionDate.split('-');

    state.executionDate = new Date(Number(dateArray[0]), Number(dateArray[1]), Number(dateArray[2]));
  }

  let model = getChildModel(paymentCurrencyDef.form, 'currency');
  if (model) {
    setState(model, 'readonly', true);
  }

  model = getChildModel(paymentPurposeDef.form, 'paymentPurpose');
  if (model) {
    setState(model, 'readonly', true);
  }

  if (props.payment.tradeId) {
    getTrade(props.payment.tradeId);
  }

  getBeneficiary(props.payment.beneficiaryId);

  setTimeout(() => {
    loadPaymentPurposeFields();
  }, 100);
}

function getBeneficiary(id: string) {
  requestManager
    .sameOrCancelAndNew('loadBeneficiary', paymentState.services.beneficiary.getBeneficiary(id))
    .subscribe(
      (res) => (
        (selectedBeneficiary.value = res), (state.search = res.name ? res.name : `${res.firstName} ${res.lastName}`)
      )
    );
}

function getTrade(id: string) {
  requestManager
    .sameOrCancelAndNew('loadTrade', paymentState.services.trade.getTrade(id))
    .subscribe((res) => setTrade(res));
}

function loadPaymentPurposeFields() {
  if (selectedWallet.value && selectedBeneficiary.value) {
    const params = {
      currency: selectedWallet.value.currency,
      beneficiaryType: selectedBeneficiary.value.type,
      bankAccountCountry: selectedBeneficiary.value.bankCountry,
    };
    requestManager
      .sameOrNewPromise(
        'loadPaymentPurpose',
        () => paymentState.store.useSettingsStore().loadBankPurposeCodes({ params }),
        params
      )
      .then((purposeCodesResponse) => {
        isPaymentPurposeRequired.value = purposeCodesResponse.mandatory;
        const purposeCodesOptions = purposeCodesResponse?.purposeCodes.map((code) => {
          return {
            value: code.purposeCode,
            label: code.purposeDescription,
          };
        });
        const validPurposeCode = purposeCodesOptions.find(
          (option) =>
            option.value === props.payment?.purposeCode || option.value === selectedBeneficiary.value?.purposeCode
        )?.value;
        const model = getChildModel(paymentPurposeDef.form, 'paymentPurpose');
        if (model) {
          setState(model, 'options', purposeCodesOptions);
        }
        currentPurposeCodesOptions.value = purposeCodesOptions;
        paymentPurposeDef.form.paymentPurpose = validPurposeCode ?? '';
      });
  }
}

function setCurrency(currency: string) {
  let wallet = state.wallets?.filter((wallet) => {
    return wallet.currency === currency;
  })[0];
  if (wallet) {
    paymentCurrencyDef.form.currency = wallet.id;
  }
}

function setTrade(trade: Trade | null) {
  if (trade && trade.id) {
    state.trade = trade;

    let buy = getBuyCcy(trade);

    setCurrency(buy.currency);
    let model = getChildModel(paymentCurrencyDef.form, 'currency');
    if (model) {
      setState(model, 'readonly', true);
    }
    setAmountMaxValidation();
  } else {
    state.trade = null;
    let model = getChildModel(paymentCurrencyDef.form, 'currency');
    if (model) {
      setState(model, 'readonly', false);
    }
  }
}

function setExecutionDate(date: Date | null) {
  state.executionDate = date;
}

function setPaymentType(type: PaymentType) {
  state.paymentType = type;

  if (type !== PaymentType.TRADE_SEND_TO_BENEFICIARY) {
    setTrade(null);
  }
}

function setAmountMaxValidation() {
  if (selectedWallet.value && state.paymentType === PaymentType.IMMEDIATE) {
    setState(getChildModel(paymentCurrencyDef.form, 'amount')!, 'max', selectedWallet.value.availableBalance);
  } else if (state.paymentType === PaymentType.TRADE_SEND_TO_BENEFICIARY && state.trade) {
    setState(getChildModel(paymentCurrencyDef.form, 'amount')!, 'max', tradeAvailableBalance);
  } else {
    setState(getChildModel(paymentCurrencyDef.form, 'amount')!, 'max', Infinity);
  }
}

function getTradeLinkedPaymentsTotalSum(tradeId: string) {
  requestManager
    .sameOrCancelAndNew(
      'getTradeLinkedPaymentsTotalSum',
      paymentState.services.payments.getTradeLinkedPaymentsTotalSum(tradeId, onBehalfOfClient.value?.id)
    )
    .subscribe((res) => (state.tradePaymentTotalSum = res?.totalAmount));
}

watch(
  () => state.wallets,
  () => {
    if (state.wallets) {
      const model = getChildModel(paymentCurrencyDef.form, 'currency');
      loadPaymentPurposeFields();
      if (model) {
        let wallets = (state.wallets ?? []).map((i: Wallet) => ({
          label: `${i.currency}`,
          value: i.id,
          balance: i.balance,
        })) as FieldOption[];
        setState(model, 'options', wallets);
      }
    }
  },
  { immediate: true }
);

watch(
  selectedBeneficiary,
  () => {
    if (selectedBeneficiary.value) {
      loadPaymentPurposeFields();
    }
  },
  { immediate: true }
);

watch(
  selectedWallet,
  () => {
    setAmountMaxValidation();
  },
  { immediate: true }
);

watch(
  () => state.paymentType,
  () => {
    setAmountMaxValidation();
  },
  { immediate: true }
);

watch(
  () => state.trade,
  () => {
    if (state.trade && state.trade.id) {
      getTradeLinkedPaymentsTotalSum(state.trade.id);
    }
  },
  { immediate: true }
);

watch(
  validations,
  () => {
    emit('update:validations', validations.value);
  },
  { immediate: true }
);

onBeforeMount(() => {
  loadWallets();
  setFormData();
});

return { __sfc: true,emit, props, requestManager, errorMessages, paymentState, onBehalfOfClient, authStore, chargeTypeForm, paymentReferenceForm, paymentReasonForm, selectedBeneficiary, currentPurposeCodesOptions, selectedPurposeCodeDescription, state, paymentCurrencyDef, paymentPurposeDef, useChargeType, bankingScheme, formsInvalid, formsLoaded, isSaving, isClient, ownerId, isPartnerUser, validations, selectedWallet, isPaymentPurposeRequired, continueDisabled, tradeAvailableBalance, tradeDisplayBalance, isCurrencyAmountInvalid, confirmPayment, savePayment, setErrorMessage, formToPaymentModel, submitForms, loadWallets, setFormData, getBeneficiary, getTrade, loadPaymentPurposeFields, setCurrency, setTrade, setExecutionDate, setPaymentType, setAmountMaxValidation, getTradeLinkedPaymentsTotalSum, BankingScheme, PaymentType, paymentTypeLabels, FeePaymentType, formatCurrencyValue, formatDate, TradeBeneficiaryInfo, CurrencyFundingFee, PaymentReferenceForm, PaymentReasonForm, BeneficiaryAccountSelector, SchedulePaymentForm, ChargeTypeForm, PaymentEditorCancelModal }
}

})